import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { debounce } from 'throttle-debounce'
import { getCategories, getContent, searchAllArticles, searchArticles } from '../../services/apis/help'
import { selectCurrentTheme } from '../../selectors/common'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import KeyboardOutlineIcon from '../../assets/icons/KeyboardOutlineIcon'
import RefreshIcon from '../../assets/icons/RefreshIcon'
import { EmptyState, EmptyStateAction } from '../common/EmptyState'
import SupportHeader from './SupportHeader'
import SupportScrollArea from './SupportScrollArea'
import SupportRow from './SupportRow'
import SupportListItem from './SupportListItem'
import SupportSearchField from './SupportSearchField'
import SectionLabel from './SectionLabel'
import SupportListSkeleton, { SupportArticleSkeleton } from './SupportListSkeleton'

type HelpArticle = { id: number | string; title: string }
type HelpFolder = { id: number | string; displayName: string; count: number; articles: HelpArticle[] }
type HelpCategory = { id: number | string; displayName: string; count: number; icon?: string; order: number; folders: HelpFolder[] }
type HelpSearchHit = { id: number | string; title: string; category: number | string; folder: number | string }

type LoadState = 'idle' | 'loading' | 'ready' | 'error'

type SearchState = {
    query: string
    hits: HelpSearchHit[]
    total: number
    state: LoadState
    /** "See all results" was pressed: paginated list instead of the top five. */
    expanded: boolean
    page: number
}

const QUICK_RESULTS = 5
const PAGE_SIZE = 20
const ANALYTICS_SOURCE = 'Help Center'

const emptySearch = (query = ''): SearchState => ({ query, hits: [], total: 0, state: 'idle', expanded: false, page: 1 })

const articleCount = (n: number) => `${n} ${n === 1 ? 'article' : 'articles'}`

type Props = {
    onBack: () => void
    onOpenShortcuts: () => void
}

/** Help center inside the Help & feedback tab: search, browse categories, read articles. */
function HelpCenterViewInner({ onBack, onOpenShortcuts }: Props) {
    const darkTheme = useSelector(selectCurrentTheme)
    const [categories, setCategories] = useState<HelpCategory[]>([])
    const [categoriesState, setCategoriesState] = useState<LoadState>('loading')
    const [category, setCategory] = useState<HelpCategory | null>(null)
    const [folder, setFolder] = useState<HelpFolder | null>(null)
    const [article, setArticle] = useState<HelpArticle | null>(null)
    const [content, setContent] = useState('')
    const [contentState, setContentState] = useState<LoadState>('idle')
    const [searchText, setSearchText] = useState('')
    const [search, setSearch] = useState<SearchState>(emptySearch())
    const searchRequestId = useRef(0)

    const loadCategories = useCallback(async () => {
        setCategoriesState('loading')
        try {
            const result = await getCategories()
            const list = (Object.values(result ?? {}) as HelpCategory[]).sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            setCategories(list)
            setCategoriesState('ready')
        } catch {
            setCategoriesState('error')
        }
    }, [])

    useEffect(() => {
        void loadCategories()
    }, [loadCategories])

    useEffect(() => {
        if (!article) {
            setContent('')
            setContentState('idle')
            return
        }
        let cancelled = false
        setContentState('loading')
        getContent({ id: String(article.id) })
            .then((result) => {
                if (cancelled) return
                setContent(result?.content ?? '')
                setContentState('ready')
            })
            .catch(() => {
                if (!cancelled) setContentState('error')
            })
        return () => {
            cancelled = true
        }
    }, [article])

    const runQuickSearch = useMemo(
        () =>
            debounce(300, async (query: string) => {
                const requestId = ++searchRequestId.current
                try {
                    const result = await searchArticles({ searchText: query, pageNo: 1, pageSize: QUICK_RESULTS })
                    if (requestId !== searchRequestId.current) return
                    setSearch({
                        query,
                        hits: result?.data ?? [],
                        total: result?.meta?.pagination?.total ?? (result?.data?.length ?? 0),
                        state: 'ready',
                        expanded: false,
                        page: 1
                    })
                } catch {
                    if (requestId === searchRequestId.current) setSearch({ ...emptySearch(query), state: 'error' })
                }
            }),
        []
    )

    useEffect(() => () => runQuickSearch.cancel(), [runQuickSearch])

    const handleSearchTextChange = useCallback(
        (value: string) => {
            setSearchText(value)
            const query = value.trim()
            if (!query) {
                runQuickSearch.cancel()
                searchRequestId.current++
                setSearch(emptySearch())
                return
            }
            AnalyticsManager.logEvent('Search Input', { input: value, source: ANALYTICS_SOURCE })
            setSearch((prev) => ({ ...prev, query, state: 'loading' }))
            runQuickSearch(query)
        },
        [runQuickSearch]
    )

    const loadSearchPage = useCallback(
        async (page: number) => {
            const query = search.query
            const requestId = ++searchRequestId.current
            setSearch((prev) => ({ ...prev, state: 'loading', expanded: true }))
            try {
                const result = await searchAllArticles({ searchText: query, page, pageSize: PAGE_SIZE })
                if (requestId !== searchRequestId.current) return
                setSearch((prev) => ({
                    query,
                    hits: page === 1 ? result?.data ?? [] : [...prev.hits, ...(result?.data ?? [])],
                    total: result?.meta?.pagination?.total ?? prev.total,
                    state: 'ready',
                    expanded: true,
                    page
                }))
            } catch {
                if (requestId === searchRequestId.current) setSearch((prev) => ({ ...prev, state: 'error' }))
            }
        },
        [search.query]
    )

    const openCategory = useCallback((next: HelpCategory) => {
        AnalyticsManager.logEvent('Category Clicked', { source: ANALYTICS_SOURCE, category: next.displayName })
        setCategory(next)
    }, [])

    const openFolder = useCallback((next: HelpFolder) => {
        AnalyticsManager.logEvent('Folder Clicked', { source: ANALYTICS_SOURCE, category: next.displayName })
        setFolder(next)
    }, [])

    const openArticle = useCallback((next: HelpArticle) => {
        AnalyticsManager.logEvent('Article Clicked', { source: ANALYTICS_SOURCE, article_title: next.title })
        setArticle(next)
    }, [])

    const openSearchHit = useCallback(
        (hit: HelpSearchHit) => {
            const hitCategory = categories.find((c) => c.id === hit.category) ?? null
            const hitFolder = hitCategory?.folders.find((f) => f.id === hit.folder) ?? null
            const hitArticle = hitFolder?.articles.find((a) => a.id === hit.id) ?? { id: hit.id, title: hit.title }
            setCategory(hitCategory)
            setFolder(hitFolder)
            openArticle(hitArticle)
            setSearchText('')
            setSearch(emptySearch())
        },
        [categories, openArticle]
    )

    const handleBack = useCallback(() => {
        if (article) setArticle(null)
        else if (folder) setFolder(null)
        else if (category) setCategory(null)
        else if (searchText) handleSearchTextChange('')
        else onBack()
    }, [article, category, folder, handleSearchTextChange, onBack, searchText])

    const title = article ? folder?.displayName ?? 'Article' : folder ? folder.displayName : category ? category.displayName : 'Help center'
    const scrollKey = `${category?.id ?? ''}/${folder?.id ?? ''}/${article?.id ?? ''}`

    const renderSearch = () => {
        if (search.state === 'loading' && search.hits.length === 0) {
            return <SupportListSkeleton rows={4} />
        }
        if (search.state === 'error') {
            return (
                <EmptyState
                    text="Search isn't available right now."
                    action={<EmptyStateAction label="Try again" icon={<RefreshIcon sx={{ fontSize: 16 }} />} onClick={() => handleSearchTextChange(searchText)} />}
                />
            )
        }
        if (search.state === 'ready' && search.hits.length === 0) {
            return <EmptyState text={`No articles match "${search.query}".`} />
        }
        const hasMore = search.expanded ? search.hits.length < search.total : search.total > search.hits.length
        return (
            <>
                <SectionLabel>{search.expanded ? `${search.total} results` : 'Results'}</SectionLabel>
                {search.hits.map((hit, i) => (
                    <SupportListItem
                        key={`${hit.id}-${i}`}
                        title={hit.title}
                        onClick={() => openSearchHit(hit)}
                        last={i === search.hits.length - 1 && !hasMore}
                    />
                ))}
                {hasMore ? (
                    search.expanded ? (
                        <Button
                            variant="outlined"
                            color="inherit"
                            size="small"
                            fullWidth
                            disabled={search.state === 'loading'}
                            onClick={() => loadSearchPage(search.page + 1)}
                            sx={{ mt: 1.5, height: 30, color: 'text.secondary' }}
                        >
                            {search.state === 'loading' ? 'Loading…' : 'Show more'}
                        </Button>
                    ) : (
                        <SupportListItem title={`See all ${search.total} results`} emphasis last onClick={() => loadSearchPage(1)} />
                    )
                ) : null}
            </>
        )
    }

    const renderRoot = () => (
        <Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
            <SupportSearchField value={searchText} onChange={handleSearchTextChange} placeholder="Search articles" />
            <Box sx={{ mt: 2 }}>
                {searchText.trim() ? (
                    renderSearch()
                ) : (
                    <>
                        <SupportRow
                            icon={<KeyboardOutlineIcon />}
                            title="Keyboard shortcuts"
                            description="Every shortcut, grouped by area"
                            onClick={onOpenShortcuts}
                            data-event="Keyboard Shortcuts Opened"
                        />
                        <SectionLabel sx={{ mt: 3 }}>Browse</SectionLabel>
                        {categoriesState === 'loading' ? (
                            <SupportListSkeleton rows={5} boxed />
                        ) : categoriesState === 'error' ? (
                            <EmptyState
                                text="Couldn't load the help center."
                                action={<EmptyStateAction label="Try again" icon={<RefreshIcon sx={{ fontSize: 16 }} />} onClick={loadCategories} />}
                            />
                        ) : categories.length === 0 ? (
                            <EmptyState text="No help articles yet." />
                        ) : (
                            <Stack spacing={0.75}>
                                {categories.map((item) => (
                                    <SupportRow
                                        key={item.id}
                                        icon={
                                            item.icon ? (
                                                <Box
                                                    component="img"
                                                    alt=""
                                                    src={item.icon}
                                                    sx={{ width: 18, height: 18, filter: darkTheme ? 'invert(1)' : 'none', opacity: 0.85 }}
                                                />
                                            ) : (
                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                                            )
                                        }
                                        title={item.displayName}
                                        description={articleCount(item.count)}
                                        onClick={() => openCategory(item)}
                                    />
                                ))}
                            </Stack>
                        )}
                    </>
                )}
            </Box>
        </Box>
    )

    const renderCategory = (current: HelpCategory) => (
        <Box sx={{ px: 2, pt: 1, pb: 2 }}>
            {current.folders.length === 0 ? (
                <EmptyState text="Nothing in this section yet." />
            ) : (
                current.folders.map((item, i) => (
                    <SupportListItem
                        key={item.id}
                        title={item.displayName}
                        meta={articleCount(item.count)}
                        onClick={() => openFolder(item)}
                        last={i === current.folders.length - 1}
                    />
                ))
            )}
        </Box>
    )

    const renderFolder = (current: HelpFolder) => (
        <Box sx={{ px: 2, pt: 1, pb: 2 }}>
            {current.articles.length === 0 ? (
                <EmptyState text="Nothing in this section yet." />
            ) : (
                current.articles.map((item, i) => (
                    <SupportListItem key={item.id} title={item.title} onClick={() => openArticle(item)} last={i === current.articles.length - 1} />
                ))
            )}
        </Box>
    )

    const renderArticle = (current: HelpArticle) => {
        const crumbs = [category?.displayName, folder?.displayName].filter(Boolean).join(' / ')
        return (
            <Box sx={{ px: 2, pt: 2, pb: 3 }}>
                {crumbs ? (
                    <Typography variant="caption" color="text.tertiary" sx={{ display: 'block' }}>
                        {crumbs}
                    </Typography>
                ) : null}
                <Typography variant="subtitle1" fontWeight={500} sx={{ mt: 0.5, mb: 2, lineHeight: 1.35 }}>
                    {current.title}
                </Typography>
                {contentState === 'loading' || contentState === 'idle' ? (
                    <SupportArticleSkeleton />
                ) : contentState === 'error' ? (
                    <EmptyState
                        text="Couldn't load this article."
                        action={<EmptyStateAction label="Try again" icon={<RefreshIcon sx={{ fontSize: 16 }} />} onClick={() => setArticle({ ...current })} />}
                    />
                ) : (
                    <Box
                        className="support-article"
                        dangerouslySetInnerHTML={{ __html: content }}
                        sx={(theme) => ({
                            fontSize: 13,
                            lineHeight: 1.6,
                            color: 'text.primary',
                            wordBreak: 'break-word',
                            '& p': { m: 0, mb: 1.25 },
                            '& h1, & h2, & h3, & h4, & h5, & h6': { fontSize: 14, fontWeight: 500, lineHeight: 1.35, mt: 2.5, mb: 0.75 },
                            '& h1': { fontSize: 16 },
                            '& h2': { fontSize: 15 },
                            '& strong, & b': { fontWeight: 500 },
                            '& > :first-of-type': { mt: 0 },
                            '& img, & video, & iframe': {
                                maxWidth: '100%',
                                height: 'auto',
                                display: 'block',
                                borderRadius: 1,
                                border: `1px solid ${theme.palette.divider}`,
                                my: 1.5
                            },
                            '& a': { color: 'var(--primary)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
                            // Tailwind's preflight removes list markers; restore them for authored content.
                            '& ul, & ol': { pl: 2.5, mt: 0, mb: 1.25 },
                            '& ul': { listStyleType: 'disc' },
                            '& ol': { listStyleType: 'decimal' },
                            '& li': { mb: 0.5 },
                            '& code': {
                                fontFamily: 'monospace !important',
                                fontSize: 12,
                                px: 0.5,
                                py: 0.125,
                                borderRadius: 0.5,
                                bgcolor: 'action.hover'
                            },
                            '& pre': { p: 1.5, borderRadius: 1, bgcolor: 'action.hover', overflowX: 'auto', mb: 1.25, '& code': { p: 0, bgcolor: 'transparent' } },
                            '& table': { width: '100%', borderCollapse: 'collapse', fontSize: 12, mb: 1.5 },
                            '& th, & td': { border: `1px solid ${theme.palette.divider}`, p: 0.75, textAlign: 'left', verticalAlign: 'top' },
                            '& th': { fontWeight: 500, bgcolor: 'action.hover' },
                            '& blockquote': { m: 0, mb: 1.25, pl: 1.5, borderLeft: `2px solid ${theme.palette.divider}`, color: 'text.secondary' },
                            '& hr': { border: 0, borderTop: `1px solid ${theme.palette.divider}`, my: 2 }
                        })}
                    />
                )}
            </Box>
        )
    }

    return (
        <>
            <SupportHeader title={title} onBack={handleBack} />
            <SupportScrollArea key={scrollKey}>
                {article ? renderArticle(article) : folder ? renderFolder(folder) : category ? renderCategory(category) : renderRoot()}
            </SupportScrollArea>
        </>
    )
}

export default memo(HelpCenterViewInner)
